import {
  isEmpty as isEmptyLodash,
  capitalize as capitalizeLodash
} from 'lodash'
import dayjs from 'dayjs'

export * from './constants'
export * from './files'
export * as history from './history'
export * from './sort'
export * from './states'
export * from './trades'

export const TERMS_URL = 'https://www.bosscathome.com/usage-policy'
export const TERMS_PDF = 'https://punchlistcms.wpengine.com/wp-content/uploads/2022/11/pro_terms.pdf'
export const RESOURCES_URL = 'https://my.bosscathome.com/pro-app'

/** MASKS *******************************/
// Máscara simple para teléfono - solo dígitos con guiones
export const PHONE_MASK_INPUT = [/[1-9]/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
export const PHONE_MASK_REGEXP = /^([0-9]+(-[0-9]+)+)$/

// Regex para validar el formato del teléfono con prefijo opcional
export const PHONE_VALIDATION_REGEXP = /^(\+\d{1,3}\s?)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/

// Función para formatear automáticamente el número mientras el usuario escribe
export const formatPhoneInput = (value: string): string => {
  if (!value) return ''
  
  // Remover todos los caracteres no numéricos
  const cleaned = value.replace(/\D/g, '')
  
  // Si empieza con 1 y tiene 11 dígitos, asumir que es +1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const areaCode = cleaned.substring(1, 4)
    const prefix = cleaned.substring(4, 7)
    const line = cleaned.substring(7)
    return `+1 (${areaCode}) ${prefix}-${line}`
  }
  
  // Si tiene 10 dígitos, formatear como número local
  if (cleaned.length === 10) {
    const areaCode = cleaned.substring(0, 3)
    const prefix = cleaned.substring(3, 6)
    const line = cleaned.substring(6)
    return `(${areaCode}) ${prefix}-${line}`
  }
  
  // Si tiene menos de 10 dígitos, devolver como está
  if (cleaned.length < 10) {
    return cleaned
  }
  
  // Si tiene más de 11 dígitos, formatear como internacional
  if (cleaned.length > 11) {
    const countryCode = cleaned.substring(0, cleaned.length - 10)
    const areaCode = cleaned.substring(countryCode.length, countryCode.length + 3)
    const prefix = cleaned.substring(countryCode.length + 3, countryCode.length + 6)
    const line = cleaned.substring(countryCode.length + 6)
    return `+${countryCode} (${areaCode}) ${prefix}-${line}`
  }
  
  return value
}

// Función para limpiar el número de teléfono y enviarlo al backend
export const cleanPhoneNumber = (phone: string): string => {
  if (!phone) return ''
  
  // Remover todos los caracteres no numéricos
  const cleaned = phone.replace(/\D/g, '')
  
  // Si tiene 10 dígitos, agregar +1 al inicio
  if (cleaned.length === 10) {
    return '+1' + cleaned
  }
  
  // Si tiene 11 dígitos y empieza con 1, agregar +
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return '+' + cleaned
  }
  
  // Si tiene más de 11 dígitos, agregar + al inicio
  if (cleaned.length > 11) {
    return '+' + cleaned
  }
  
  return cleaned
}

// Función para formatear el número de teléfono para mostrar
export const formatPhoneDisplay = (phone: string): string => {
  if (!phone) return ''
  
  // Limpiar el número
  const cleaned = phone.replace(/\D/g, '')
  
  if (cleaned.length === 10) {
    // Formato sin prefijo de país: (XXX) XXX-XXXX
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // Formato con prefijo de país +1: +1 (XXX) XXX-XXXX
    return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`
  } else if (cleaned.length > 11) {
    // Formato con prefijo de país internacional: +XX (XXX) XXX-XXXX
    const countryCode = cleaned.substring(0, cleaned.length - 10)
    const areaCode = cleaned.substring(countryCode.length, countryCode.length + 3)
    const number = cleaned.substring(countryCode.length + 3)
    return `+${countryCode} (${areaCode}) ${number.substring(0, 3)}-${number.substring(3)}`
  }
  
  return phone
}
/****************************************/

/** EMAILS ******************************/
export const validateEmail = (email: string): boolean => {
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}
/****************************************/

/** PASSWORDS ***************************/
export const containsLowercase = (str: string): boolean => /[a-z]/.test(str)
export const containsUppercase = (str: string): boolean => /[A-Z]/.test(str)
export const containsNumber = (str: string): boolean => /\d/.test(str)
export const containsLetter = (str: string): boolean => /[A-Za-z]/.test(str)
export const containsNumberLetter = (str: string): boolean => containsNumber(str) && containsLetter(str)

export const validatePassword = (psw: string): boolean => (
  psw.length >= 6 &&
  containsUppercase(psw) &&
  containsLowercase(psw) &&
  containsNumberLetter(psw)
)
/****************************************/

/** OBJECTS *****************************/
export const isEmpty = (object: any): boolean => {
  if (typeof object === 'string') { return isEmptyLodash(object.trim()) }
  return isEmptyLodash(object)
}
/****************************************/

/** STRINGS *****************************/
export const capitalize = (string?: string | undefined): string => {
  const stringCopy = string?.trim().replaceAll('_', ' ')
  return capitalizeLodash(stringCopy)
}

export const formatPhone = (phone: number | null): string => {
  if (phone !== null) {
    const phoneCopy = phone.toString()
    if (phoneCopy.length === 10) {
      return phoneCopy.substring(0, 3).concat('-').concat(phoneCopy.substring(3, 6)).concat('-').concat(phoneCopy.substring(6))
    }
  }
  return ''
}

export const formatTimestamp = (timestamp: number | null, format: string): string => {
  if (timestamp !== null) {
    return dayjs.unix(timestamp).format(format)
  }
  return ''
}

export const concatSeparator = (fstStr: string, sndStr: string, separator: string): string => {
  return fstStr?.concat(fstStr && !isEmpty(fstStr) && sndStr && !isEmpty(sndStr) ? separator : '', sndStr || '')
}

export const errorTextTryingTo = (text: string): string => {
  if (!isEmpty(text)) return 'An error occurred trying to '.concat(text).concat(". We'll fix it soon!")
  return "An unexpected error occurred. We'll fix it soon!"
}

export const parseUrl = (url: string, rest: string): string => {
  if (!isEmpty(url)) return url.endsWith('/') ? url.concat(rest) : url.concat('/', rest)
  return ''
}
/****************************************/

/** NUMBERS *****************************/

export const round = (value: number, exp: number) => {
  if (value === null || value === undefined) return '0.'.concat('0'.repeat(exp))

  const roundedPrice = (
    Math.round((value + Number.EPSILON) * 100) / 100
  ).toFixed(2)
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
  })
  return formatter.format(Number(roundedPrice))
}
